import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import logo from "@/assets/logo-mente-sa.png";
import { useCmsContent } from "@/hooks/use-cms-content";

const Auth = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [isForgot, setIsForgot] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { content } = useCmsContent();
  const brandLogo = (content.logo_url as string) || logo;
  const brandName = (content.business_name as string) || "Mente Sã";
  const redirectTo = searchParams.get("redirect") || null;
  const autoCheckout = searchParams.get("checkout") === "1";

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const PROD_ORIGIN = "https://mentesa.pt";
    const origin = window.location.hostname === "mentesa.pt" || window.location.hostname === "www.mentesa.pt"
      ? window.location.origin
      : PROD_ORIGIN;
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${origin}/reset-password`,
    });
    setLoading(false);
    if (error) {
      toast({ title: "Erro", description: error.message, variant: "destructive" });
    } else {
      toast({
        title: "Email enviado",
        description: "Verifique a sua caixa de entrada para redefinir a palavra-passe.",
      });
      setIsForgot(false);
      setIsLogin(true);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    if (isLogin) {
      const cleanEmail = email.trim().toLowerCase();
      console.log("Tentativa de login:", cleanEmail);
      const { data: loginData, error } = await supabase.auth.signInWithPassword({ 
        email: cleanEmail, 
        password: password.trim() 
      });
      if (error) {
        if (error.message.includes("Email not confirmed")) {
          toast({ 
            title: "Email não confirmado", 
            description: "Por favor, verifique a sua caixa de entrada e confirme o seu email antes de entrar.", 
            variant: "destructive" 
          });
        } else {
          toast({ 
          title: "Credenciais incorretas", 
          description: "Verifique se o email e a palavra-passe estão corretos. Note que para entrar na Área do Cliente é necessário ter criado uma palavra-passe ao preencher a sua ficha.", 

            variant: "destructive" 
          });
        }
      } else if (loginData.user) {
        // Check if user is admin
        const { data: roleData } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", loginData.user.id)
          .eq("role", "admin")
          .maybeSingle();
        
        if (roleData) {
          navigate("/admin");
          return;
        }

        // Check if user is a staff member
        const { data: staffData } = await supabase
          .from("staff")
          .select("id")
          .eq("user_id", loginData.user.id)
          .eq("is_active", true)
          .maybeSingle();

        if (staffData) {
          navigate("/colaborador");
          return;
        }

        navigate(redirectTo || "/");
      }
    } else {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { full_name: fullName, phone } },
      });
      if (error) {
        toast({ title: "Erro", description: error.message, variant: "destructive" });
      } else {
        toast({
          title: "Conta criada!",
          description: "Verifique o seu email para confirmar a conta.",
        });
        setIsLogin(true);
      }
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <a href="/" className="inline-flex items-center gap-3 mb-4">
            <img src={brandLogo} alt={brandName} className="h-16 w-16 rounded-full object-cover shadow-md" />
            <span className="font-display text-3xl font-bold text-primary">{brandName}</span>
          </a>
          <p className="text-muted-foreground text-sm px-6">
            {isForgot ? "Recuperar palavra-passe" : isLogin ? "Entre com o email e a palavra-passe que definiu ao preencher a sua ficha." : "Crie a sua conta"}
          </p>
        </div>

        {isForgot ? (
          <form onSubmit={handleForgot} className="rounded-2xl bg-card border border-border p-8 space-y-5">
            <p className="text-sm text-muted-foreground text-center">
              Introduza o seu email e enviaremos um link para redefinir a palavra-passe.
            </p>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@exemplo.com" required />
            </div>
            <Button type="submit" className="w-full rounded-full" disabled={loading}>
              {loading ? "A enviar..." : "Enviar link de recuperação"}
            </Button>
            <p className="text-center text-sm text-muted-foreground">
              <button type="button" onClick={() => setIsForgot(false)} className="text-primary font-semibold hover:underline">
                Voltar ao login
              </button>
            </p>
          </form>
        ) : (
          <form onSubmit={handleSubmit} className="rounded-2xl bg-card border border-border p-8 space-y-5">
            {!isLogin && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="fullName">Nome completo</Label>
                  <Input id="fullName" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="O seu nome" required={!isLogin} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Telefone</Label>
                  <Input id="phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+351 912 345 678" required={!isLogin} />
                </div>
              </>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@exemplo.com"
                required
                className="lowercase"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Palavra-passe</Label>
                {isLogin && (
                  <button
                    type="button"
                    onClick={() => setIsForgot(true)}
                    className="text-xs text-primary hover:underline"
                  >
                    Esqueceu-se?
                  </button>
                )}
              </div>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                minLength={6}
              />
            </div>
            <Button type="submit" className="w-full rounded-full" disabled={loading}>
              {loading ? "A processar..." : isLogin ? "Entrar" : "Criar conta"}
            </Button>


            {/* Removido o login social para evitar confusão com integração de calendário */}


            <p className="text-center text-sm text-muted-foreground">
              {isLogin ? "Ainda não tem conta?" : "Já tem conta?"}{" "}
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="text-primary font-semibold hover:underline"
              >
                {isLogin ? "Registar" : "Entrar"}
              </button>
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default Auth;
